import { cn } from '@spiaggeit/spit-ui'
import { type KeyboardEvent, type MouseEvent, useId } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckboxIndicator } from '@/components/Checkbox/CheckboxIndicator'
import {
  PolicyTextContent,
  PolicyTextContentProps,
} from '@/components/PolicyTextContent'

export const AB_MANDATORY_PRIVACY_CHECKBOX_CLASS =
  'ab-mandatory-privacy-checkbox'

export interface PolicyAcceptanceCheckboxProps extends PolicyTextContentProps {
  checked: boolean
  hasError?: boolean
  onCheckedChange(checked: boolean): void
  className?: string
}

export const PolicyAcceptanceCheckbox = ({
  checked,
  hasError = false,
  onCheckedChange,
  className,
  ...policyTextProps
}: PolicyAcceptanceCheckboxProps) => {
  const { t } = useTranslation()
  const labelId = useId()
  const errorId = useId()

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.code === 'Space') {
      e.preventDefault()
    }

    if (['NumpadEnter', 'Enter', 'Space'].includes(e.code)) {
      onCheckedChange(!checked)
    }
  }

  const handleClick = (e: MouseEvent) => {
    if ((e.target as HTMLElement).closest('a, button')) {
      return
    }

    onCheckedChange(!checked)
  }

  return (
    <div
      className={cn('flex flex-col gap-1', className)}
      data-testid="policy-acceptance-checkbox"
    >
      <div
        className={cn(
          'relative inline-flex cursor-pointer items-start gap-2',
          AB_MANDATORY_PRIVACY_CHECKBOX_CLASS
        )}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
      >
        <CheckboxIndicator
          checked={checked}
          hasError={hasError}
          labelledBy={labelId}
        />

        <span className="text-xs leading-5 text-secondary" id={labelId}>
          <PolicyTextContent variant="checkbox" {...policyTextProps} />
        </span>
      </div>

      {hasError && (
        <p className="text-sm text-red-600" id={errorId} role="alert">
          {t('summary.policyAcceptanceRequired')}
        </p>
      )}
    </div>
  )
}
